import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import withRouter from "../../../components/withRouter";
import { authClient } from "../../../lib/auth-client";
import { toast } from "react-toastify";
import { Loader2, ShieldCheck } from "lucide-react";
import { Button } from "../../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { FieldDescription } from "../../../components/ui/field";
import { Input } from "../../../components/ui/input";

const VerifyEmail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email") || "";

  const [otp, setOtp] = useState("");
  const [loader, setLoader] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState("");

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otp.trim()) {
      setError("Por favor, insira o código de verificação");
      return;
    }
    if (!email) {
      setError("E-mail não informado");
      return;
    }

    setLoader(true);
    setError("");

    try {
      const { error: apiError } = await authClient.emailOtp.verifyEmail({
        email,
        otp: otp.trim(),
      });

      if (apiError) {
        setError(apiError.message || "Código inválido ou expirado");
      } else {
        toast.success("E-mail verificado com sucesso!");
        navigate("/login");
      }
    } catch {
      setError("Erro ao verificar e-mail");
    } finally {
      setLoader(false);
    }
  };

  const handleResend = async () => {
    if (!email) {
      toast.error("E-mail não informado");
      return;
    }

    setResending(true);
    try {
      await authClient.emailOtp.sendVerificationOtp({
        email,
        type: "email-verification",
      });
      toast.success("Novo código enviado para seu e-mail!");
    } catch {
      toast.error("Erro ao reenviar código");
    } finally {
      setResending(false);
    }
  };

  document.title = "Verificar e-mail | Ad Pulse";

  return (
    <React.Fragment>
      <div className="relative isolate min-h-screen bg-white text-gray-900">
        <div className="flex min-h-[calc(100vh-88px)] items-center justify-center px-4 pb-10">
          <Card className="w-full max-w-md rounded-2xl border border-gray-200 bg-white shadow-sm">
            <CardHeader className="space-y-1 pb-4 pt-7 text-center">
              <CardTitle className="text-xl font-bold text-gray-900 sm:text-3xl">
                Verificar e-mail
              </CardTitle>
              <CardDescription className="text-sm text-gray-500">
                Insira o código de verificação enviado para{" "}
                {email ? (
                  <strong className="text-gray-700">{email}</strong>
                ) : (
                  "seu e-mail"
                )}
              </CardDescription>
              <div className="mt-3 flex justify-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-500">
                  <ShieldCheck className="h-6 w-6" />
                </div>
              </div>
            </CardHeader>

            <CardContent className="space-y-5 px-4 pb-5 sm:px-7 sm:pb-7">
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-center text-xs text-amber-800 sm:text-sm">
                Enviamos um código de 6 dígitos para o seu e-mail. Verifique
                também a pasta de spam.
              </div>

              <form onSubmit={handleVerify} className="space-y-5">
                <div>
                  <label
                    className="mb-1.5 block text-sm font-medium text-gray-700"
                    htmlFor="otp"
                  >
                    Código de verificação
                  </label>
                  <Input
                    id="otp"
                    name="otp"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="000000"
                    maxLength={6}
                    value={otp}
                    onChange={(e) => {
                      setOtp(e.target.value.replace(/\D/g, ""));
                      setError("");
                    }}
                    className={`h-11 rounded-lg border-slate-200 bg-slate-50 text-center text-lg tracking-[0.5em] text-gray-700 placeholder:text-gray-400 placeholder:tracking-[0.5em] ${
                      error
                        ? "!border-red-500 focus-visible:!ring-red-500"
                        : "focus-visible:ring-red-500/20"
                    }`}
                  />
                  {error ? (
                    <FieldDescription className="mt-1 flex items-center gap-1 !text-red-600">
                      <span className="inline-flex size-3.5 shrink-0 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold leading-none text-white">
                        !
                      </span>
                      {error}
                    </FieldDescription>
                  ) : null}
                </div>

                <Button
                  type="submit"
                  disabled={loader}
                  className="mt-2 h-11 w-full rounded-lg border-0 bg-red-600 font-semibold text-white hover:bg-red-700 active:bg-red-800"
                >
                  {loader ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : null}
                  Verificar e-mail
                </Button>
              </form>

              <div className="pt-1 text-center">
                <Button
                  type="button"
                  variant="ghost"
                  disabled={resending}
                  onClick={handleResend}
                  className="text-sm font-medium text-red-600 hover:text-red-700"
                >
                  {resending ? (
                    <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                  ) : null}
                  Reenviar código
                </Button>
              </div>

              <div className="text-center">
                <p className="text-xs text-gray-500 sm:text-sm">
                  <Link
                    to="/login"
                    className="font-semibold text-red-600 underline underline-offset-4 hover:text-red-700"
                  >
                    Voltar para o login
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        <footer className="pb-4 text-center text-xs text-gray-500">
          &copy; {new Date().getFullYear()} Ad Pulse. Feito com{" "}
          <span className="text-red-500">&#10084;</span>
        </footer>
      </div>
    </React.Fragment>
  );
};

export default withRouter(VerifyEmail);
